<template>
	<section class="color-palette">
		<img :src="backgroundUrl" hidden ref="colorPaletteHiddenPlaceholder" crossorigin="anonymous"/>
		<p v-if="colors.length === 0" class="loading">Loading Color Palette…</p>
		<ul v-else>
			<li v-for="color in colors" :key="color.name"
				:style="{ 'background-color': selectedColor.name === color.name ? selectedColor.hsla : color.hsla, 'border-color': overrideBorder ? overrideBorder : selectedColor.name === color.name ? selectedColor.bodyTextColor : color.bodyTextColor}"
				:class="{'u--selected': selectedColor.name === color.name}"
				@click="$emit('color-change', color);$emit('color-change-manual', color)"
				@dblclick="$emit('picker-active', true)">
			</li>
			<verte-color-picker :active="pickerActive" :color="selectedColor" @close="$emit('picker-active', false)"></verte-color-picker>
		</ul>
	</section>
</template>

<script>
import * as Vibrant from 'node-vibrant'
import ColorThief from 'colorthief/dist/color-thief'
import VerteColorPicker from '../shared/VerteColorPicker'

const IMAGE_LOAD_TIMEOUT = 30000 // 30 seconds

export default {
	name: "ColorPalette",
	props: {
		background: Object,
		selectedColor: { type: Object, default: () => {} },
		overrideBorder: { type: String, default: null },
		variant: { type: String, default: 'ColorTheif' },
		numberOfColors: { type: Number, default: 9 },
		submission: Boolean,
		pickerActive: Boolean
	},
	components: {
		VerteColorPicker
	},
	data() {
		return {
			colors: [],
			backgroundUrl: '',
			currentOperation: null,
		}
	},
	created() {
		this.setBackgroundUrl()
	},
	mounted() {
		this.createColorPalette(this.background)
	},
	beforeDestroy() {
		this.cancelPendingOperation()
	},
	watch: {
		background(){
			this.colors = []
			this.setBackgroundUrl()
			this.createColorPalette(this.background)
		},
	},
	methods: {
		cancelPendingOperation() {
			if (this.currentOperation) {
				this.currentOperation.cancelled = true
				this.currentOperation = null
			}
		},
		setBackgroundUrl(){
			this.backgroundUrl = this.background.download_url || this.background.large_url || this.background.preview_url || this.background.thumbnail_url
		},
		createColorPalette(background) {
			switch (this.variant) {
				case 'Vibrant':
					this.createVibrantColorPalette(background)
					break
				default:
					this.createColorThiefColorPalette(background)
					break
			}
		},
		createVibrantColorPalette(background) {
			if (background.background_guid !== this.background.background_guid) {
				return;
			} else if (background && background.colorsVibrant) {
				this.setFirstColor(background)
			} else if (background && !background.colorsVibrant) {
				this.cancelPendingOperation()

				const operation = { cancelled: false }
				this.currentOperation = operation

				// Add timeout to prevent infinite loading
				const timeout = new Promise((_, reject) =>
					setTimeout(() => reject(new Error('Vibrant palette timeout')), IMAGE_LOAD_TIMEOUT)
				)

				Promise.race([
					// TODO: this direct vendor fetch may break if the host blocks CORS; options include mirroring curated photos to momentum.photos or fronting them with a simple Cloudflare/edge proxy.
					Vibrant.from(this.backgroundUrl).getPalette(),
					timeout
				])
					.then(palette => {
						if (operation.cancelled) return
						background.colorsVibrant = Object.keys(palette).map(swatch => {
							const hsl = palette[swatch].getHsl()
							const rgb = palette[swatch].getRgb()
							return {
								name: swatch,
								hex: palette[swatch].getHex(),
								rgba:`rgb(${rgb[0].toFixed(0)}, ${rgb[1].toFixed(0)}, ${rgb[2].toFixed(0)})`,
								hsla: `hsl(${(hsl[0] * 360).toFixed()}, ${(hsl[1] * 100).toFixed()}%, ${(hsl[2] * 100).toFixed()}%)`,
								titleTextColor: palette[swatch].getTitleTextColor(),
								bodyTextColor: palette[swatch].getBodyTextColor()
							}
						})
						if (operation.cancelled) return
						this.setFirstColor(background)
					})
					.catch((error) => {
						if (operation.cancelled) return
						console.error('Failed to generate Vibrant palette:', error)
						// Set a default gray color palette on error
						background.colorsVibrant = [{
							name: 'Gray',
							hex: '#808080',
							rgba: 'rgb(128, 128, 128)',
							hsla: 'hsl(0, 0%, 50%)',
							titleTextColor: '#ffffff',
							bodyTextColor: '#ffffff'
						}]
						this.setFirstColor(background)
					})
			}
		},
		createColorThiefColorPalette(background) {
			if (background && background.colors) {
				this.setFirstColor(background)
			} else if (background && !background.colors) {
				this.cancelPendingOperation()

				const operation = { cancelled: false }
				this.currentOperation = operation

				const img = this.$refs.colorPaletteHiddenPlaceholder
				img.background_guid = background.background_guid
				const colorThief = new ColorThief()

				// Add timeout to prevent infinite loading
				const timeout = new Promise((_, reject) =>
					setTimeout(() => reject(new Error('Image load timeout')), IMAGE_LOAD_TIMEOUT)
				)

				Promise.race([this.onLoadImg(img), timeout])
					.then(() => {
						if (operation.cancelled) return
						this.parseColors(colorThief.getPalette(img, this.numberOfColors), background)
						this.setFirstColor(background)
					})
					.catch((error) => {
						if (operation.cancelled) return
						console.error('Failed to load image for color palette:', error)
						// Set a default gray color palette on error
						background.colors = [{
							rgba: 'rgb(128, 128, 128)',
							hex: '#808080',
							name: '#808080',
							hsla: 'hsl(0, 0%, 50%)',
						}]
						this.setFirstColor(background)
					})
			}
		},
		onLoadImg(img){
			return new Promise((resolve, reject) => {
				const handleLoad = () => {
					cleanup()
					resolve()
				}
				const handleError = (error) => {
					cleanup()
					reject(error)
				}
				const cleanup = () => {
					img.removeEventListener('load', handleLoad)
					img.removeEventListener('error', handleError)
				}

				img.addEventListener('load', handleLoad)
				img.addEventListener('error', handleError)
			})
		},
		parseColors(colors, background){
			if (background.background_guid !== this.background.background_guid) {
				return;
			}
			background.colors = colors.map((color) => {
				const hsl = Vibrant.Util.rgbToHsl(color[0], color[1], color[2])
				return {
					rgba:`rgb(${color[0]}, ${color[1]}, ${color[2]})`,
					hex: Vibrant.Util.rgbToHex(color[0], color[1], color[2]),
					name: Vibrant.Util.rgbToHex(color[0], color[1], color[2]),
					hsla: `hsl(${(hsl[0] * 360).toFixed()}, ${(hsl[1] * 100).toFixed()}%, ${(hsl[2] * 100).toFixed()}%)`,
				}
			})
		},

		setFirstColor(background) {
			if (background.background_guid !== this.background.background_guid) {
				return;
			}

			switch (this.variant) {
				case 'Vibrant':
					this.colors = background.colorsVibrant
					break
				default:
					this.colors = background.colors
					break
			}

			if (!background.widget_color || background.widget_color === 'null') {
				this.$emit('color-change', this.colors[0])
			} else if (background.widget_color) {
				try {
					this.$emit('color-change', JSON.parse(background.widget_color))
				} catch (e) {
					this.$emit('color-change', background.widgetColor)
				}
			}
		},
	}
}
</script>

<style scoped>
	.color-palette { padding-top: 5px; margin-right:10px; }
		.color-palette ul { display: block; list-style: none; padding: 0; }
		.color-palette li { flex: 1; margin: 0.2em; text-align: center; cursor: pointer;  height: 49px; width: 49px; border-radius: 49%; float: left; }



	.u--selected { border: solid white 1px; }
</style>
